// src/middleware.tsx
import {
  createContext,
  useContext,
  useEffect,
  useState,
  ReactNode,
} from "react";
import {
  Outlet,
  useNavigate,
  useLocation,
  NavigateFunction,
  Location,
} from "react-router-dom";
import { routes } from "./consts";

const i18n = {
  locales: ["en", "ar"] as const,
  defaultLocale: "en" as const,
};

type Locale = (typeof i18n.locales)[number];

const protectedRoutes = ["/admin"] as const;

interface User {
  id: number;
  name: string;
}

// Auth Context Type
interface AuthContextType {
  user: User | null;
  setUser: (user: User | null) => void;
}

// Locale Context Type
interface LocaleContextType {
  locale: Locale;
  setLocale: (locale: Locale) => void;
}

// Auth Context
const AuthContext = createContext<AuthContextType>({
  user: null,
  setUser: () => {},
});

// Locale Context
const LocaleContext = createContext<LocaleContextType>({
  locale: i18n.defaultLocale,
  setLocale: () => {},
});

// Protected Route Component
export function ProtectedRoute() {
  const { user } = useContext(AuthContext);
  const navigate: NavigateFunction = useNavigate();
  const location: Location = useLocation();

  useEffect(() => {
    const isProtected = protectedRoutes.some((route) =>
      location.pathname.includes(route)
    );

    if (isProtected && !user) {
      navigate(routes.login, { replace: true });
    }
  }, [user, location, navigate]);

  return user ? <Outlet /> : null;
}

// App Provider Props
interface AppProviderProps {
  children: ReactNode;
}

// App Provider
export function AppProvider({ children }: AppProviderProps) {
  const [locale, setLocale] = useState<Locale>(i18n.defaultLocale);
  const navigate: NavigateFunction = useNavigate();
  const location: Location = useLocation();

  useEffect(() => {
    const pathnameLocale = location.pathname.split("/")[1] as Locale;
    const pathnameHasLocale = i18n.locales.some(
      (loc) =>
        location.pathname.startsWith(`/${loc}/`) ||
        location.pathname === `/${loc}`
    );

    if (!pathnameHasLocale || location.pathname === "/") {
      const defaultLocale = locale ?? "en";
      navigate(
        `/${defaultLocale}${
          location.pathname === "/" ? "" : location.pathname
        }`,
        { replace: true }
      );
    } else if (i18n.locales.includes(pathnameLocale)) {
      setLocale(pathnameLocale);
      document.documentElement.dir = pathnameLocale === "ar" ? "rtl" : "ltr";
    }
  }, [location, navigate]);

  const localeValue: LocaleContextType = { locale, setLocale };

  return (
    <LocaleContext.Provider value={localeValue}>
      {children}
    </LocaleContext.Provider>
  );
}

// Custom hooks
export const useLocale = (): LocaleContextType => useContext(LocaleContext);
